export interface Point {
  x: number
  y: number
}

/**
 * Cardinal-spline path through `pts`, giving the soft curves the design uses
 * rather than the hard elbows of a polyline.
 *
 * Control points are clamped to [minY, maxY]. A cardinal spline overshoots on a
 * sharp change — a peak followed by a dip sends the curve past the value on both
 * sides — and anything outside the viewBox is clipped by the SVG viewport, which
 * breaks the line mid-flight. A Bézier stays inside the convex hull of its
 * control points, so clamping those is what keeps the whole curve on screen.
 */
export function smoothPath(pts: Point[], minY: number, maxY: number, tension = 0.2): string {
  if (pts.length < 2) return ''
  const clamp = (y: number) => Math.min(Math.max(y, minY), maxY)
  let d = `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`
  for (let i = 0; i < pts.length - 1; i++) {
    const prev = pts[i - 1] ?? pts[i]
    const curr = pts[i]
    const next = pts[i + 1]
    const after = pts[i + 2] ?? next
    const c1x = curr.x + (next.x - prev.x) * tension
    const c1y = clamp(curr.y + (next.y - prev.y) * tension)
    const c2x = next.x - (after.x - curr.x) * tension
    const c2y = clamp(next.y - (after.y - curr.y) * tension)
    d += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${next.x.toFixed(2)},${next.y.toFixed(2)}`
  }
  return d
}

/** Straight-segment path — used where the design draws a jagged daily series. */
export function linePath(pts: Point[]): string {
  if (pts.length === 0) return ''
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')
}

/**
 * Straight-segment path that breaks at gaps.
 *
 * A null point is unknown, not zero — a period before the source had data, or
 * one still in the future — so the line lifts rather than dropping to the floor
 * and back. Joining across a gap would draw a straight run through days nobody
 * measured and make it look like a measurement.
 */
export function gappedLinePath(pts: (Point | null)[]): string {
  let d = ''
  let penDown = false
  for (const p of pts) {
    if (!p) {
      penDown = false
      continue
    }
    d += `${penDown ? ' L' : ' M'}${p.x.toFixed(2)},${p.y.toFixed(2)}`
    penDown = true
  }
  return d.trim()
}
